import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { injectIntl } from 'react-intl';
import { Col, Row, Modal } from 'react-bootstrap';
import { FacilityInfo } from 'components';
import { Core as ReviewButton } from '../../../../website/widgets/dynamic/ReviewButton';
import StarRack from '../../../../website/components/stateless/StarRack';
import Translation from '../../../../website/components/stateless/Translation';
import classes from './ReviewsModal.scss';

const propTypes = {
  facility: PropTypes.shape({
    id: PropTypes.string.isRequired,
    reviews: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      rating: PropTypes.number.isRequired,
      timestamp: PropTypes.string.isRequired,
      name: PropTypes.string,
      body: PropTypes.string,
      reply: PropTypes.shape({
        body: PropTypes.string,
        timestamp: PropTypes.string.isRequired,
      }),
    })),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  sortOrder: PropTypes.string,
  onOrderChange: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
};

const defaultProps = {
  sortOrder: 'highest',
};

const ReviewsModal = ({
  facility,
  onClose,
  sortOrder,
  onOrderChange,
  show,
  intl,
}) => {
  const getReviews = () => {
    const { reviews } = facility;

    return reviews.slice().sort((left, right) => {
      switch (sortOrder) {
        case 'newest':
          return right.timestamp.localeCompare(left.timestamp);
        case 'highest':
          return right.rating - left.rating;
        case 'lowest':
          return left.rating - right.rating;
        default:
          // highest
          return right.rating - left.rating;
      }
    });
  };

  const handleOrderChange = ({ target }) => {
    onOrderChange(target.value);
  };

  const renderReply = (reply) => {
    if (!reply) {
      return null;
    }

    return (
      <div className={classes.reply}>
        <div className={classes.heading}>
          <div className={classes.authorDate}>
            <Translation
              id="widgets.internalReviews.reviewsModal.replyAuthor"
              defaultMessage="Reply from Manager"
            >
              {text => <span className={classes.author}>{text}</span>}
            </Translation>
            {' '}
            <Translation
              id="widgets.internalReviews.reviewsModal.replyDate"
              defaultMessage="on {date}"
              values={{
                date: intl.formatDate(reply.timestamp, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }),
              }}
            />
          </div>
        </div>
        <p>
          {reply.body}
        </p>
      </div>
    );
  };

  const renderReview = review => (
    <li key={new Date(review.timestamp).getTime().toString()} className={classes.review}>
      <div className={classes.heading}>
        <StarRack className={classes.starRack} rating={review.rating} />
        <div className={classes.authorDate}>
          <span className={classes.author}>{review.name}</span>
          {' '}
          <Translation
            id="widgets.internalReviews.reviewsModal.replyDate"
            defaultMessage="on {date}"
            values={{
              date: intl.formatDate(review.timestamp, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }),
            }}
          />
        </div>
      </div>
      <p>
        {review.body}
      </p>
      {renderReply(review.reply)}
    </li>
  );

  return (
    <Modal show={show} keyboard onHide={onClose} className={classes.modalTitle}>
      <Modal.Header closeButton>
        <Modal.Title className="color-primary-foreground">
          <Translation id="widgets.internalReviews.reviewsModal.title" defaultMessage="Reviews" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={classes.modalBody}>
        <Row>
          <Col sm={12} md={7}>
            <FacilityInfo facility={facility} showPhone={false} />
          </Col>

          <Col sm={12} md={5}>
            <ReviewButton facilityId={facility.id}>
              <Translation
                id="widgets.internalReviews.reviewsModal.writeReviewButton"
                defaultMessage="Write a Review"
              />
            </ReviewButton>
          </Col>
        </Row>
        <Row className={classes.controlRow}>
          <Col sm={12} md={6} className={classes.rating}>
            <StarRack className={classes.starRack} rating={facility.rating} showUnfilled />
            {' '}
            <Translation
              id="widgets.internalReviews.reviewsModal.reviewsCount"
              defaultMessage="{count, number} {count, plural, one {review} other {reviews}}"
              values={{
                count: facility.reviews.length,
              }}
            />
          </Col>
          <Col sm={12} md={6} className="text-end">
            <Translation id="widgets.internalReviews.reviewsModal.sort" defaultMessage="Sort by:">
              {text => (
                <label htmlFor="sort" className={classes.sortLabel}>
                  {text}
                </label>
              )}
            </Translation>
            {' '}
            <select name="sort" value={sortOrder} onChange={handleOrderChange} className={classes.options}>
              <Translation
                id="widgets.internalReviews.reviewsModal.sortNewest"
                defaultMessage="Newest"
              >
                {text => <option value="newest">{text}</option>}
              </Translation>
              <Translation
                id="widgets.internalReviews.reviewsModal.sortHighest"
                defaultMessage="Hightest rated"
              >
                {text => <option value="highest">{text}</option>}
              </Translation>
              <Translation
                id="widgets.internalReviews.reviewsModal.sortLowest"
                defaultMessage="Lowest rated"
              >
                {text => <option value="lowest">{text}</option>}
              </Translation>
            </select>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <ul className={`list-unstyled ${classes.reviewList}`}>
              {getReviews().map(renderReview)}
            </ul>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

ReviewsModal.propTypes = propTypes;
ReviewsModal.defaultProps = defaultProps;

ReviewsModal.fragments = {
  facility: gql`
    fragment ReviewsModalFacility on Facility {
      rating
      reviews {
        rating
        timestamp
        name
        body
        reply {
          body
          timestamp
        }
      }
      ...FacilityInfo
    }

    ${FacilityInfo.fragments.facility}
  `,
};

export default injectIntl(ReviewsModal);
